import { Experiment, Variant } from '@marvelapp/react-ab-test';
import { useRef, useState } from 'react';

import Accordion from '@perpay-web/components/base/Accordion/Accordion';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { Html } from '@perpay-web/components/base/Html/Html';
import Portal from '@perpay-web/components/base/Portal/Portal';
import {
    DISABLED_VARIANT,
    ENABLED_VARIANT,
    PAYROLL_PHONE_HELP,
    UNVERIFIED_PAYROLL_PHONE_HELP,
} from '@perpay-web/constants/experiments';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import ExperimentContainer from '@perpay-web/fintech/components/base/Experiment/ExperimentContainer';
import CopyButton from '@perpay-web/fintech/components/composite/CopyButton/CopyButton';
import { PAYROLL_PHONE_HELP_EXPERIMENT_ENABLED } from '@perpay-web/fintech/constants/flags';
import { noop } from '@perpay-web/utils/noop';
import { IS_JEST } from '@perpay-web/utils/testHelpers';

import styles from './PayrollInstructions.scss';

// TODO: determine how to handle companies that have perpay_form_only enabled

// Use Regular Expressions to extract the account number and routing transit number.
// The goal is to inject some React-rendered UI next to the numbers to help copy them.
// We capture any wrapper tags (if present) to avoid affecting the style of the new UI or
// the existing styling of the numbers.
//
// 555 followed by five or more digits
const ACCOUNT_NUMBER_RE = /(<[^>]+>)?\s*(555\d{5,})\s*(<\/[^>]+>)?/;
// Routing numbers are a limited set and known.
// Some routing umbers are 9 digits and some account numbers 9 digits, so we need to
// replace and wrap the account number first (since it always begins with 555)
// before we replace and wrap the routing number.
const RTN_RE =
    /(<[^>]+>)?\s*(0?1110309|0?11103093|0?21409169|0?36001808|121000248|23607380|236073801)\s*(<\/[^>]+>)?/;

const ACCOUNT_NUMBER_ID = 'instructions-account-number';
const ACCOUNT_NUMBER_COPY_PORTAL_ID = 'instructions-account-number-copy';
const RTN_ID = 'instructions-rtn';
const RTN_COPY_PORTAL_ID = 'instructions-rtn-copy';
// We insert spans with IDs to serve as render-roots for our React clipboard copy components.
// This allows us to reuse our React code instead of complexifying the vanilla HTML.
const addCopyWrapper = (html) => {
    if (!html) {
        return html;
    }

    const accountNumberReplaceString = `
        <span id="${ACCOUNT_NUMBER_ID}">$&</span>
        <span id="${ACCOUNT_NUMBER_COPY_PORTAL_ID}"></span>
    `;
    const rtnReplaceString = `
        <span id="${RTN_ID}">$&</span>
        <span id="${RTN_COPY_PORTAL_ID}"></span>
    `;
    return html
        .replace(ACCOUNT_NUMBER_RE, accountNumberReplaceString)
        .replace(RTN_RE, rtnReplaceString);
};

const getInnerText = (element) => element.innerText;
const getAccountNumberElement = (element) =>
    element.querySelector(`#${ACCOUNT_NUMBER_ID}`);
const getRtnElement = (element) => element.querySelector(`#${RTN_ID}`);

const PayrollInstructions = ({
    instructionsType,
    companyIsOther,
    companyFormEnabled = false,
    payrollWorksWithPerpay = null,
    directDepositFormUrl = null,
    otherInstructionsAccordionHeading = null,
    otherInstructionsAccordionHTML = null,
    worksForVerifiedCompany,
    otherCompanyPayrollInstructions = '',
    payrollProviderInstructions = null,
    inboundPhoneTestEligible,
    onClickNeedHelp,
    onClickFinished,
    launchSelfHelpToolHandler,
}) => {
    const instructionsRef = useRef(null);
    const [showGenericInstructions, setShowGenericInstructions] =
        useState(false);

    const toggleInstructionsType = () => {
        setShowGenericInstructions((prev) => !prev);
    };

    const heading =
        instructionsType === 'stop'
            ? 'Stop your direct deposit'
            : 'Set up direct deposit to receive your order';

    const showingGenericInstructions =
        companyIsOther || showGenericInstructions;

    const showDirectDepositFormButton =
        companyFormEnabled && payrollWorksWithPerpay && directDepositFormUrl;

    const accordionContentPresent =
        otherInstructionsAccordionHeading && otherInstructionsAccordionHTML;

    const showAccordion = showingGenericInstructions && accordionContentPresent;

    // This payroll provider has been noted to work with perpay, so the instructions
    // are real instructions with a generic alternative, rather than "let down" instructions
    const showPayrollProviderInstructionsIncorrect =
        !companyIsOther && payrollWorksWithPerpay;

    // We are running inbound phone tests for both verified and unverified
    // users with independent experiments and sample rates, but the same
    // UX.
    const inboundPhoneTestType = worksForVerifiedCompany
        ? PAYROLL_PHONE_HELP
        : UNVERIFIED_PAYROLL_PHONE_HELP;

    const instructions = showingGenericInstructions
        ? otherCompanyPayrollInstructions
        : payrollProviderInstructions;

    const copyButtonPortals =
        !IS_JEST && instructionsType === 'start' ? (
            <>
                <Portal parentId={ACCOUNT_NUMBER_COPY_PORTAL_ID} tagName='span'>
                    <CopyButton
                        className={styles['copy-button']}
                        action='inline-link'
                        textElementRef={instructionsRef}
                        childGetter={getAccountNumberElement}
                        valueGetter={getInnerText}
                    />
                </Portal>
                <Portal parentId={RTN_COPY_PORTAL_ID} tagName='span'>
                    <CopyButton
                        className={styles['copy-button']}
                        action='inline-link'
                        textElementRef={instructionsRef}
                        childGetter={getRtnElement}
                        valueGetter={getInnerText}
                    />
                </Portal>
            </>
        ) : null;

    return (
        <div className={styles['payment-instructions__container']}>
            <div className={styles['payment-instructions']}>
                <div className={styles.heading}>{heading}</div>
                <div className={styles.instructions__container}>
                    {/* NOTE:
                     * This is a hacky way to make sure the Portals continue working
                     * when switching between the specific and the generic instructions.
                     */}
                    {instructions && !showingGenericInstructions
                        ? copyButtonPortals
                        : null}
                    {instructions && showingGenericInstructions
                        ? copyButtonPortals
                        : null}
                    <Html
                        ref={instructionsRef}
                        className={styles.instructions}
                        markup={addCopyWrapper(instructions)}
                    />
                    {showAccordion ? (
                        <Accordion
                            className={styles.accordion}
                            heading={
                                <div className={styles.accordion__heading}>
                                    {otherInstructionsAccordionHeading}
                                </div>
                            }
                        >
                            <Html
                                className={styles.accordion__body}
                                markup={otherInstructionsAccordionHTML}
                            />
                        </Accordion>
                    ) : null}
                    {PAYROLL_PHONE_HELP_EXPERIMENT_ENABLED &&
                    inboundPhoneTestEligible ? (
                        <ExperimentContainer name={inboundPhoneTestType}>
                            <Variant name={ENABLED_VARIANT}>
                                <CustomButton
                                    action='secondary'
                                    onClick={() => onClickNeedHelp()}
                                    className={styles.button}
                                >
                                    Need help setting up payment?
                                </CustomButton>
                            </Variant>
                            <Variant name={DISABLED_VARIANT} />
                        </ExperimentContainer>
                    ) : null}
                    <CustomButton
                        action='primary'
                        onClick={() => onClickFinished()}
                        className={styles.button}
                    >
                        I&rsquo;m finished!
                    </CustomButton>
                    <ul className={styles['extra-links']}>
                        {showPayrollProviderInstructionsIncorrect ? (
                            <li>
                                <CustomButton
                                    action='inline-link'
                                    onClick={() => toggleInstructionsType()}
                                    size='small'
                                    className={styles['extra-links__link']}
                                >
                                    Are these instructions incorrect?
                                </CustomButton>
                            </li>
                        ) : null}
                        {showDirectDepositFormButton ? (
                            <li>
                                <CustomLink
                                    action='inline-link'
                                    href={directDepositFormUrl}
                                    onClick={noop}
                                    target='_blank'
                                    className={styles['extra-links__link']}
                                >
                                    Need a form to give to your payroll
                                    department?
                                </CustomLink>
                            </li>
                        ) : null}
                        {PAYROLL_PHONE_HELP_EXPERIMENT_ENABLED &&
                        inboundPhoneTestEligible ? (
                            <Experiment name={inboundPhoneTestType}>
                                <Variant name={DISABLED_VARIANT}>
                                    <li>
                                        <CustomButton
                                            action='inline-link'
                                            onClick={() =>
                                                launchSelfHelpToolHandler()
                                            }
                                            size='small'
                                            className={
                                                styles['extra-links__link']
                                            }
                                        >
                                            Need Help?
                                        </CustomButton>
                                    </li>
                                </Variant>
                                <Variant name={ENABLED_VARIANT} />
                            </Experiment>
                        ) : (
                            <li>
                                <CustomButton
                                    action='inline-link'
                                    onClick={() => launchSelfHelpToolHandler()}
                                    size='small'
                                    className={styles['extra-links__link']}
                                >
                                    Need Help?
                                </CustomButton>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PayrollInstructions;
